import { useEffect, useState } from 'react';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { ProgressBars } from '@domgen/base-design-system-2.0/molecules/ProgressBars';
import { DatePicker } from '@domgen/base-design-system-2.0/molecules/DatePicker';
import { ArrowLeft } from '../assets/icons/ArrowLeft';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { store } from '../store/store';
import { StepKey } from '../enums/stepKey';
import { FormKey } from '../enums/formKey';
import { gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { usePostHog } from '@posthog/react';
import { getSystemCode } from '../utils/getSystemCode';
import { formatLocalisedDate } from '@domgen/shared/utils/date/formatLocalisedDate';
import { parseLocalDateString } from '@domgen/shared/utils/date/parseLocalDateString';
import { toLocalDateString } from '@domgen/shared/utils/date/toLocalDateString';
import { capturePostHogEvent } from '../services/posthog/capture';
import { useApplianceFormData } from '../store/selectors/useApplianceFormData';
import { useAppState } from '../store/selectors/useAppState';
import { useUrlParams } from '../store/selectors/useUrlParams';
import { PageBackgroundWrapper } from '../components/PageBackgroundWrapper';
import { formCategoryName } from '../utils/store/formCategoryName';
import { formApplianceName } from '../utils/store/formApplianceName';
import { StoreEvent } from '../enums/storeEvent';

type PurchaseDatePageProps = {
  stepNumber: number;
  totalSteps: number;
};

export function ArrivalDatePage({ stepNumber, totalSteps }: PurchaseDatePageProps) {
  const formData = useApplianceFormData();
  const { session } = useAppState();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const [date, setDate] = useState<Date | undefined>(
    formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE]
      ? parseLocalDateString(formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE])
      : undefined,
  );
  const posthog = usePostHog();
  const applianceName = formApplianceName.get();
  const applianceCategory = formCategoryName.get();

  // Min date is set to 8 years
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 8);

  useEffect(() => {
    gaPageView({
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appSessionID: session?.id,
      pagename: 'purchase-date',
    });
  }, []);

  useEffect(() => {
    if (date) {
      const formattedDate = formatLocalisedDate(date, 'en-US');
      gaEvent({
        eventCategory: 'appliance details form USA',
        eventAction: 'purchaseDate',
        eventLabel: `${formattedDate}`,
        category: 'registration-pages',
        type: 'STANDARD',
        applianceCategory,
        applianceName,
        appSessionID: session?.id,
        pagename: 'purchase-date',
        regSystemCode: systemCode,
      });
    }
  }, [date]);

  const handleContinue = () => {
    gaEvent({
      eventCategory: 'Continue button',
      eventAction: 'Continue',
      eventLabel: date ? 'success' : 'failed',
      category: 'registration-pages',
      type: 'STANDARD',
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
      pagename: 'purchase-date',
      regSystemCode: systemCode,
    });

    capturePostHogEvent(posthog, 'date-continue-cta-clicked', store.get());

    if (!date) {
      return;
    }

    store.send({
      type: StoreEvent.NEXT,
      data: {
        [StepKey.ARRIVAL_DATE]: {
          purchaseDate: toLocalDateString(date),
        },
      },
    });
  };

  const handleBack = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'GoBack CTA',
      eventLabel: 'success',
      category: 'registration-pages',
      type: 'STANDARD',
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
      pagename: 'purchase-date',
      regSystemCode: systemCode,
    });
    capturePostHogEvent(posthog, 'date-back-clicked', store.get());
    store.send({ type: StoreEvent.BACK, data: { stepKey: StepKey.ARRIVAL_DATE } });
  };

  return (
    <PageBackgroundWrapper>
      <ProgressBars total={totalSteps} current={stepNumber} />

      <h1 className="my-(--spacing-space-400) text-h3">When did it arrive at your home?</h1>
      <p className="mb-4">
        This is an estimate of the date the appliance arrived at where it’s installed.
      </p>

      <div className="flex flex-col gap-2">
        <DatePicker
          value={date}
          onChange={setDate}
          minDate={minDate}
          maxDate={new Date()}
          label="Arrival date"
          errors={{
            maxDate: 'Please enter a valid arrival date',
            invalidDate: 'Please enter a valid arrival date',
          }}
        />
      </div>

      <div className="flex flex-col w-full mt-4 items-center md:flex-row-reverse md:justify-between">
        <div className="w-full md:w-fit mb-4">
          <Button onClick={handleContinue}>Continue</Button>
        </div>
        <TextLink onClick={handleBack} leftIcon={<ArrowLeft />}>
          Go Back
        </TextLink>
      </div>
    </PageBackgroundWrapper>
  );
}
